import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Users, 
  Building2, 
  DollarSign, 
  UserCheck, 
  Copy, 
  ExternalLink,
  Clock,
  TrendingUp
} from "lucide-react";
import { api } from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function PartnerDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get(`${API_URL}/api/partner/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching partner dashboard:", error);
      toast.error("Failed to load partner dashboard");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const fullUrl = `${window.location.origin}${dashboardData.partner.referral_link}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success("Referral link copied to clipboard!");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#547d91]"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Unable to load partner dashboard</p>
      </div>
    );
  }

  const { partner, stats, users } = dashboardData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Partner Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Welcome back, {partner.name}
            {partner.company && <span className="text-slate-400"> • {partner.company}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
            <span className="text-sm text-slate-600">Your referral code:</span>
            <code className="font-mono font-semibold text-[#547d91]">{partner.referral_code}</code>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={copyReferralLink}
            className="gap-2"
            data-testid="copy-referral-link-btn"
          >
            <Copy className="w-4 h-4" />
            Copy Link
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Signups</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total_users}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Users</p>
                <p className="text-3xl font-bold text-slate-900">{stats.active_users}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {stats.total_users > 0 
                    ? `${Math.round((stats.active_users / stats.total_users) * 100)}% of total`
                    : "0%"}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Properties</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total_properties}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Portfolio Value</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(stats.total_portfolio_value)}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Referred Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-2">No users have signed up yet</p>
              <p className="text-sm text-slate-400">
                Share your referral link to start growing your network
              </p>
              <Button
                variant="outline"
                className="mt-4 gap-2"
                onClick={copyReferralLink}
              >
                <Copy className="w-4 h-4" />
                Copy Referral Link
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Signed Up</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Properties</TableHead>
                    <TableHead className="text-right">Portfolio Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell className="text-slate-600">{user.email}</TableCell>
                      <TableCell className="text-slate-600">{user.mobile || "-"}</TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(user.last_login)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                          <Building2 className="w-3 h-3" />
                          {user.property_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(user.total_portfolio_value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
